import { Controller } from "@hotwired/stimulus"

const ACTIVE_CLASSES = ["bg-accent", "border-accent", "text-white"]
const DEFAULT_CLASSES = [
  "bg-white", "border-cream-300", "text-neutral-700",
  "hover:border-green-700", "hover:text-green-900"
]
const CLASS_PARAMS = ["safety_class", "interaction_class"]

export default class extends Controller {
  static targets = ["panel", "trigger", "chevron", "chip", "badge", "clear"]
  static values = { frameId: { type: String, default: "herb-results" } }

  connect() {
    this._frameRenderHandler = (event) => {
      if (event.target.id === this.frameIdValue) this.syncFromUrl()
    }
    document.addEventListener("turbo:frame-render", this._frameRenderHandler)
  }

  disconnect() {
    document.removeEventListener("turbo:frame-render", this._frameRenderHandler)
  }

  toggle() {
    if (this.panelTarget.hasAttribute("hidden")) {
      this._open()
    } else {
      this._close()
    }
  }

  closeOnOutsideClick(event) {
    if (this.panelTarget.hasAttribute("hidden")) return
    if (this.element.contains(event.target)) return
    this._close()
  }

  closeOnEscape() {
    if (!this.panelTarget.hasAttribute("hidden")) this._close()
  }

  _open() {
    this.panelTarget.removeAttribute("hidden")
    this.triggerTarget.setAttribute("aria-expanded", "true")
    if (this.hasChevronTarget) this.chevronTarget.classList.add("rotate-180")
  }

  _close() {
    this.panelTarget.setAttribute("hidden", "")
    this.triggerTarget.setAttribute("aria-expanded", "false")
    if (this.hasChevronTarget) this.chevronTarget.classList.remove("rotate-180")
  }

  // The panel lives outside the herb-results frame, so it is not
  // re-rendered when a chip (or the search box / A–Z nav) navigates the
  // frame. After each frame render, re-derive every chip's active state
  // and toggle href, plus the badge and clear link, from the URL — the
  // same URL-sync pattern the search and A–Z nav controllers use.
  syncFromUrl() {
    const params = new URLSearchParams(window.location.search)

    this.chipTargets.forEach((chip) => {
      const isActive = params.get(chip.dataset.kind) === chip.dataset.value
      this._setChipActive(chip, isActive)
      chip.href = this._toggleHref(params, chip.dataset.kind, chip.dataset.value, isActive)
    })

    this._updateBadge(CLASS_PARAMS.filter((key) => params.has(key)).length)
    this._updateClear(params)
  }

  _setChipActive(chip, isActive) {
    chip.setAttribute("aria-pressed", isActive ? "true" : "false")
    chip.classList.remove(...(isActive ? DEFAULT_CLASSES : ACTIVE_CLASSES))
    chip.classList.add(...(isActive ? ACTIVE_CLASSES : DEFAULT_CLASSES))
  }

  _toggleHref(params, kind, value, isActive) {
    const next = new URLSearchParams(params)
    if (isActive) next.delete(kind)
    else next.set(kind, value)
    return this._pathWithParams(next)
  }

  _updateBadge(count) {
    if (!this.hasBadgeTarget) return
    this.badgeTarget.textContent = count
    this.badgeTarget.toggleAttribute("hidden", count === 0)
  }

  _updateClear(params) {
    if (!this.hasClearTarget) return
    this.clearTarget.toggleAttribute("hidden", !CLASS_PARAMS.some((key) => params.has(key)))
    const cleared = new URLSearchParams(params)
    CLASS_PARAMS.forEach((key) => cleared.delete(key))
    this.clearTarget.href = this._pathWithParams(cleared)
  }

  _pathWithParams(params) {
    // A changed filter means a different result list, so the current page number no longer applies
    params.delete("page")
    const query = params.toString()
    return query ? `${window.location.pathname}?${query}` : window.location.pathname
  }
}
